import { Dialog, DialogHeader, DialogContent, DialogFooter } from '../ui/Dialog'
import { Button } from '../ui/button'
import {
  Buildings,
  MapPin,
  Envelope,
  Phone,
  IdentificationCard,
  CheckCircle,
  XCircle,
  Calendar,
  CurrencyDollar,
  Tag,
} from '@phosphor-icons/react'
import { CNPJData, formatCNPJ } from '../../services/brasilapi'

interface CNPJPreviewModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (data: CNPJData) => void
  data: CNPJData | null
  loading?: boolean
}

export function CNPJPreviewModal({
  isOpen,
  onClose,
  onConfirm,
  data,
  loading = false,
}: CNPJPreviewModalProps) {
  if (!data) return null

  const situacaoColor = data.descricao_situacao_cadastral?.toLowerCase() === 'ativa'
    ? 'text-green-600 bg-green-50 border-green-200'
    : 'text-red-600 bg-red-50 border-red-200'

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value)
  }

  const formatPhone = (ddd: string) => {
    if (!ddd) return null
    const cleaned = ddd.replace(/\D/g, '')
    if (cleaned.length >= 10) {
      return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 6)}-${cleaned.slice(6)}`
    }
    return ddd
  }

  const formatDate = (dateStr: string) => {
    if (!dateStr) return null
    const date = new Date(dateStr)
    return date.toLocaleDateString('pt-BR')
  }

  return (
    <Dialog isOpen={isOpen} onClose={onClose} maxWidth="lg">
      <DialogHeader>
        <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
          <IdentificationCard size={24} className="text-emerald-600" />
          Dados do CNPJ Encontrados
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          Confira os dados abaixo e confirme para preencher o formulário
        </p>
      </DialogHeader>

      <DialogContent className="max-h-[60vh] overflow-y-auto">
        <div className="space-y-6">
          {/* Status Badge */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">CNPJ: {formatCNPJ(data.cnpj)}</span>
            <span className={`px-3 py-1 rounded-full text-sm font-medium border ${situacaoColor}`}>
              {data.descricao_situacao_cadastral || 'Situação desconhecida'}
            </span>
          </div>

          {/* Main Company Info */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Buildings size={24} className="text-emerald-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-gray-900 text-lg">
                  {data.razao_social}
                </h3>
                {data.nome_fantasia && (
                  <p className="text-gray-600 mt-0.5">
                    {data.nome_fantasia}
                  </p>
                )}
                <p className="text-sm text-gray-500 mt-1">
                  {data.natureza_juridica}
                </p>
              </div>
            </div>
          </div>

          {/* Grid of Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Contact Info */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <Phone size={16} />
                Contato
              </h4>
              <div className="bg-white border border-gray-200 rounded-lg p-3 space-y-2">
                {data.ddd_telefone_1 && (
                  <div className="flex items-center gap-2 text-sm">
                    <Phone size={14} className="text-gray-400" />
                    <span>{formatPhone(data.ddd_telefone_1)}</span>
                  </div>
                )}
                {data.ddd_telefone_2 && (
                  <div className="flex items-center gap-2 text-sm">
                    <Phone size={14} className="text-gray-400" />
                    <span>{formatPhone(data.ddd_telefone_2)}</span>
                  </div>
                )}
                {data.email && (
                  <div className="flex items-center gap-2 text-sm">
                    <Envelope size={14} className="text-gray-400" />
                    <span className="break-all">{data.email.toLowerCase()}</span>
                  </div>
                )}
                {!data.ddd_telefone_1 && !data.email && (
                  <span className="text-sm text-gray-400">Nenhum contato informado</span>
                )}
              </div>
            </div>

            {/* Address */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <MapPin size={16} />
                Endereço
              </h4>
              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <p className="text-sm">
                  {data.tipo_logradouro} {data.logradouro}, {data.numero}
                  {data.complemento && ` - ${data.complemento}`}
                </p>
                <p className="text-sm text-gray-600">
                  {data.bairro}
                </p>
                <p className="text-sm text-gray-600">
                  {data.municipio} - {data.uf}
                </p>
                <p className="text-sm text-gray-500">
                  CEP: {data.cep?.replace(/(\d{5})(\d{3})/, '$1-$2')}
                </p>
              </div>
            </div>

            {/* Business Info */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <Tag size={16} />
                Atividade Principal
              </h4>
              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <p className="text-sm font-medium">CNAE: {data.cnae_fiscal}</p>
                <p className="text-sm text-gray-600">{data.cnae_fiscal_descricao}</p>
              </div>
            </div>

            {/* Financial/Other Info */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <CurrencyDollar size={16} />
                Informações Adicionais
              </h4>
              <div className="bg-white border border-gray-200 rounded-lg p-3 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Capital Social:</span>
                  <span className="font-medium">{formatCurrency(data.capital_social)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Porte:</span>
                  <span className="font-medium">{data.porte}</span>
                </div>
                {data.data_inicio_atividade && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Início Atividade:</span>
                    <span className="font-medium">{formatDate(data.data_inicio_atividade)}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Warning if not active */}
          {data.descricao_situacao_cadastral?.toLowerCase() !== 'ativa' && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
              <XCircle size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800">Atenção: CNPJ não está ativo</p>
                <p className="text-sm text-amber-700 mt-1">
                  A situação cadastral deste CNPJ é "{data.descricao_situacao_cadastral}".
                  Verifique se deseja continuar com o cadastro.
                </p>
              </div>
            </div>
          )}
        </div>
      </DialogContent>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          type="button"
          onClick={() => onConfirm(data)}
          disabled={loading}
          className="gap-2"
          style={{ backgroundColor: 'rgb(5, 150, 105)' }}
        >
          <CheckCircle size={20} />
          Usar estes dados
        </Button>
      </DialogFooter>
    </Dialog>
  )
}
